import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";

type Theme = "light" | "dark";

interface LuagateThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  /** 明暗を指して選ぶ (ヘッダーの選び窓が使う) */
  setTheme: (theme: Theme) => void;
}

const LuagateThemeContext = createContext<LuagateThemeContextValue>({
  theme: "light",
  toggleTheme: () => {},
  setTheme: () => {},
});

const STORAGE_KEY = "luagate-theme";

export function LuagateThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>("light");

  // Apply stored theme after hydration to avoid mismatch
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored && stored !== "light") {
      setTheme(stored);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Cleanup: remove data-theme when leaving luagate
  useEffect(() => {
    return () => {
      document.documentElement.removeAttribute("data-theme");
    };
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  return (
    <LuagateThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </LuagateThemeContext.Provider>
  );
}

export function useLuagateTheme() {
  return useContext(LuagateThemeContext);
}
